import React, { useEffect, useState } from 'react';
import { FormInput } from '../merchants/FormInput';
import { useTranslation } from 'react-i18next';
import { PERSONAL_INFORMATION_INPUTS } from '../../constants/profile-settings/personal-information';
import { useFormik } from 'formik';
import {
	convertToInitial, getFormattedInitialProfileSettingsData, getFormattedProfileSettingsFormFields,
	getProfileSettingsDetailsSchema,
	profileSettingsInitialValues
} from '../../constants/profile-settings/profile-settings-form';
import { successAlert } from '../ToastMessage';
import { SectionWrapper } from '../SectionWrapper';
import { isEqual } from 'lodash';
import { errorHandler } from '../../utils/newApplicationUtils';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { useIdentity } from '../../store/identyti/identity-context';
import { fetchSecurityInformation, setPersonalInformation } from '../../store/identyti/actions/security';

export const ProfileSettingsPersonalInformation = (): JSX.Element => {
	const { t } = useTranslation();
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const { identityState: { securityData }, dispatch } = useIdentity();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const INPUTS = PERSONAL_INFORMATION_INPUTS();
	const formik = useFormik({
		initialValues: profileSettingsInitialValues,
		validationSchema: getProfileSettingsDetailsSchema(t),
		onSubmit: () => void 0

	});

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	useEffect(() => {
		const data = convertToInitial(securityData);
		formik.setValues(data);
	}, []);

	const handleEditClick = async (): Promise<void> => {
		formik.handleSubmit();
		if (formik.isValid) {
			try {
				const params = getFormattedProfileSettingsFormFields(formik.values);
				const initialData = getFormattedInitialProfileSettingsData(securityData);
				if (!isEqual(params, initialData)) {
					setIsLoading(true);
					await setPersonalInformation(params);
					await fetchSecurityInformation(dispatch, securityData?.email);
					successAlert({
						title: 'Personal information was saved!'
					});
				}
				setIsEditMode(!isEditMode);
				setIsLoading(false);

			} catch (e) {
				console.log(e);
				errorHandler({ e, formik });
				setIsLoading(false);
			}
		}
	};

	const handleCancelClick = (): void => {
		const changedData = getFormattedProfileSettingsFormFields(formik.values);
		const initialData = getFormattedInitialProfileSettingsData(securityData);
		const dataBeforeChange = convertToInitial(securityData);
		if (!isEqual(changedData, initialData)) {
			setModalState({
				isOpen: true,
				modalTitle: t('MERCHANT.CANCEL_MODAL_TITLE'),
				modalText: t('MERCHANT.CANCEL_MODAL_TEXT'),
				confirmButtonText: t('MERCHANT.CANCEL_MODAL_CONFIRM_BTN_NAME'),
				onConfirm: closeModal,
				onCancel: (): void => {
					closeModal();
					setIsEditMode(false);
					formik.setValues(dataBeforeChange);
				}
			});
		} else {
			setIsEditMode(false);
		}
	};

	return (
		<>
			<SectionWrapper
				title="Personal information"
				sectionAdditionalClass="mb-5 min-height-200"
				isEditMode={isEditMode}
				setIsEditMode={setIsEditMode}
				onEditClick={handleEditClick}
				onCancelClick={handleCancelClick}
				loading={isLoading}
				// pageLoading={isMerchantDataLoading}
				isCardBody
				isEditBlock
			>
				<div className="row">
					{
						INPUTS.map(item => (
							<FormInput
								key={item.key}
								formik={formik}
								{...item}
								isEditMode={isEditMode}
							/>
						))
					}
				</div>
			</SectionWrapper>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
		</>
	);
};
